import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Card, Pagination, Spin, Table, Tag } from "antd";
import { formatDateWithTimeHandler } from "utils/common";
import {
    itemRender,
    pageSizeOptions,
} from "utils/table";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import AllSalaryAddPopup from "../../../../components/modelpopup/AllSalaryAddPopup";
import DeleteModal from "../../../../components/modelpopup/DeleteModal";
import ResumeListFilter from "../components/ResumeListFilter";

import { getJobDriveSlotsApi } from "api/services/jobs";
import { exportResumeApi, getResumeDetailApi } from "api/services/resume";
import FilterDrawer from "components/Drawer/resumeFilterDrawer";
import { SkeltonLoader } from "components/skeltonLoader";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import AssignSlotModal from "../../../../components/Administration/Jobs/JobDrive/AssignSlotModal";
import SelectSlotModal from "../../../../components/Administration/Jobs/JobDrive/SelectSlotModal";
import ResumeStatusModal from "../../../../components/modelpopup/resumeStatusModalPopup";
import { useResume } from "../context/resumeContext";




const AllResume = () => {
  const {
    loading,
    searchText,
    resumeData,
    view,
    setView,
    resumeModal,
    setResumeModal,
    pageSettings,
    handleGridChange,
    handleChange,
    handleSearchChange,
    handleFilterDrawer,
    showFilterDrawer,

    handleFilterChange,
    filters,
    handleInputChange,
    handleApplyFilter,
    filterLoading,
    
    handleResumeDetailPage,
    handleDateChange,
    handleClearAll,
    getResumeList,
    isFilterDirty
  } = useResume();


  const [selectedResume, setSelectedResume] = useState([]);
  const [resumeStatusModal, setResumeStatusModal] = useState(false);
  const [expandedRowData, setExpandedRowData] = useState({});
  const [expandLoading, setExpandLoading] = useState({});
  const [exportLoading, setExportLoading] = useState({});
  const [assignSlotModalOpen, setAssignSlotModalOpen] = useState(false);
  const [selectSlotModalOpen, setSelectSlotModalOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedApplicantForSlot, setSelectedApplicantForSlot] = useState(null);
  const [jobDriveSlots, setJobDriveSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const { user, userLoading } = useSelector((state) => state.user);

  // Check if user has job_drive permission
  const hasJobDrivePermission = useMemo(() => {
    if (!user?.role?.permissions) return false;
    return user.role.permissions.some((p) => p.code === "job_drive") ||
           user?.modules?.some?.((m) => m.code === "job_drive");
  }, [user]);

  // Debug: Log permission check
  useEffect(() => {
    if (user?.role?.permissions) {
      const permissionCodes = user.role.permissions.map(p => p.code);
      console.log("ResumeList - User permissions:", permissionCodes);
      console.log("ResumeList - Looking for 'job_drive':", permissionCodes.includes("job_drive"));
      console.log("ResumeList - Has job_drive permission:", hasJobDrivePermission);
    }
  }, [user, hasJobDrivePermission]);

  // Fix dropdown positioning when it opens
  useEffect(() => {
    const handleDropdownShow = (e) => {
      const dropdown = e.target.closest('.dropdown');
      if (dropdown) {
        const dropdownMenu = dropdown.querySelector('.dropdown-menu');
        if (dropdownMenu) {
          const rect = dropdown.getBoundingClientRect();
          dropdownMenu.style.position = 'fixed';
          dropdownMenu.style.top = `${rect.bottom + window.scrollY}px`;
          dropdownMenu.style.right = `${window.innerWidth - rect.right}px`;
          dropdownMenu.style.left = 'auto';
          dropdownMenu.style.zIndex = '9999';
        }
      }
    };

    const handleDropdownHide = (e) => {
      const dropdown = e.target.closest('.dropdown');
      if (dropdown) {
        const dropdownMenu = dropdown.querySelector('.dropdown-menu');
        if (dropdownMenu) {
          dropdownMenu.style.position = '';
          dropdownMenu.style.top = '';
          dropdownMenu.style.right = '';
          dropdownMenu.style.left = '';
        }
      }
    };

    // Listen for Bootstrap dropdown events
    document.addEventListener('show.bs.dropdown', handleDropdownShow);
    document.addEventListener('hide.bs.dropdown', handleDropdownHide);

    return () => {
      document.removeEventListener('show.bs.dropdown', handleDropdownShow);
      document.removeEventListener('hide.bs.dropdown', handleDropdownHide);
    };
  }, []);
 
  // Function to fetch applicant details when row is expanded
  const fetchApplicantDetails = async (id) => {
    setExpandLoading(prev => ({ ...prev, [id]: true }));
    try {
      const response = await getResumeDetailApi(id, {});
      setExpandedRowData(prev => ({ ...prev, [id]: response.data }));
    } catch (error) {
      console.error("Error fetching applicant details:", error);
    } finally {
      setExpandLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  // Function to export resume data as PDF
  const handleExportResume = async (applicantId) => {
    setExportLoading(prev => ({ ...prev, [applicantId]: true }));
    try {
      const response = await exportResumeApi(applicantId);
      
      // Create blob from response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `applicant_details_${applicantId}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting resume:", error);
      // You can add a toast notification here if needed
    } finally {
      setExportLoading(prev => ({ ...prev, [applicantId]: false }));
    }
  };

  // Render expanded content
  const expandedRowRender = (record) => {
    const id = record.id;
    const data = expandedRowData[id];
    
    if (expandLoading[id]) {
      return (
        <div className="p-2 text-center">
          <Spin tip="Loading question answers..." />
        </div>
      );
    }
    if ((!data || !data.answers || data.answers.length === 0) && !data.is_internal_referral) {
      return (
        <div className="p-2">
          <p className="text-muted mb-0 small">No question answers available for this applicant.</p>
        </div>
      );
    }

    const formatAnswer = (answer) => {
      if (answer === "True") {
        return "Yes";
      } else if (answer === "False") {
        return "No";
      } else if (typeof answer === "boolean") {
        return answer ? "Yes" : "No";
      }
      return answer;
    };
    
    return (
      <div className="p-2">
        <Card 
          title={<span className="fs-6">{data?.is_internal_referral ? "Notes" : "Applicant's Question Answers"}</span>} 
          className="mb-0 compact-card"
          bodyStyle={{ padding: '8px' }}
          headStyle={{ padding: '6px 12px', minHeight: '32px' }}
        >
         
          {
            data?.is_internal_referral && (
              <div className="mb-1">
                <div className="question small me-2 text-mute">Why do you want to refer this candidate?</div>
                <span className="   answer small text-primary">{data.note_by_user}</span>
              </div>
            )
          }
          <div className="question-answers">
            {data.answers.map((answer, index) => (
              <div key={index} className="qa-item d-flex align-items-baseline py-1 border-bottom">
                <div className="question small me-2 text-muted" style={{ flex: '0 0 50%' }}>
                  {answer.question}:
                </div>
                <div className="answer small text-primary">
                  {formatAnswer(answer.answer) || "No answer"}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  const columns = [
    {
      title: "Applicant Name",
      dataIndex: "first_name",
      sorter: true,
      render: (text, record) => (
        <span className="table-avatar">
          {record.first_name} {record.last_name}
        </span>
      ) 
    },
   
    {
      title: "Job Title",
      dataIndex: "job",
      sorter: true,
      render: (text, record) => (
        <span className="table-avatar">
          {record.job.title}
        </span>
      )   
    
    },
    {
      title: "Applied On",
      dataIndex: "created_at",
      sorter: true,
      render: (text, record) => (
        <span className="table-avatar">
          {formatDateWithTimeHandler(record.created_at)}
        </span>
      )   
    
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: true,
      render: (text, record) => (
        <span className="table-avatar">
          {record.email}
        </span>
      )   
    
    },
  
  
    {
      title: "Phone",
      dataIndex: "phone",
      sorter: true,
      render: (text, record) => (
        <span className="table-avatar">
          {record.phone }
        </span>
      )   
    },
    {
      title: "Nationality",
      dataIndex: "nationality",
      sorter: true,
      render: (text, record) => (
        <span className="table-avatar">
          {record?.nationality?.name }
        </span>
      )   
    },
    {
      title: "Residency Status",
      dataIndex: "residence_status",
      sorter: true,
      render: (text, record) => (
        <span className="table-avatar">
          <span className="badge text-bg-secondary">{record?.residence_status}</span>
          {/* {record?.residence_status } */}
        </span>
      )   
    },
    // {
    //   title: "Experience",
    //   dataIndex: "experience",
    //   sorter: true,
    //   render: (text, record) => (
    //     <span className="table-avatar">
    //     {record?.experience } year(s)
    //   </span>
    //   )   
    // },
    {
      title: "Gender",
      dataIndex: "gender",
      sorter: true,
      render: (text, record) => (
        <span className="table-avatar">
          <span className="badge text-bg-secondary">{record?.gender}</span>
          {/* {record?.residence_status } */}
        </span>
      )   
    },
    {
      title: "Referral",
      dataIndex: "is_internal_referral",
      sorter: true,
      render: (text, record) => {
        const isReferral = record?.is_internal_referral;
        const referredBy = record?.referred_by;
        
        return (
          <div className="table-avatar">
            <div className="mb-1">
              <span className={`badge ${isReferral ? 'text-bg-success' : 'text-bg-secondary'}`}>
                {isReferral ? 'Yes' : 'No'}
              </span>
            </div>
            {isReferral && referredBy && (
              <div className="small text-muted">
                <i className="fa fa-user me-1"></i>
                {referredBy.first_name} {referredBy.last_name}
                {referredBy.emp_no && (
                  <span className="ms-1">({referredBy.emp_no})</span>
                )}
              </div>
            )}
          </div>
        );
      }   
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: true,
      render: (text, record) => (
        <div className="dropdown action-label text-center">
        <span className={`badge badge-soft-${ text == "applied" ?  "warning" : text == "rejected" ? "danger" : "success"}`}>{text}</span>  
  
  
        </div>
      )   
    },
    ...(hasJobDrivePermission ? [{
      title: "Job Drive Time Slot",
      dataIndex: "job_drive_invitation",
      render: (text, record) => {
        // Only show this column if the job has job_drive_enabled
        if (!record?.job?.job_drive_enabled) {
          return <span className="table-avatar">-</span>;
        }
        
        // Check if there's a job_drive_invitation with a slot
        if (record?.job_drive_invitation?.slot) {
          const slot = record.job_drive_invitation.slot;
          return (
            <span 
              className="table-avatar"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedApplicantForSlot(record);
                setSelectSlotModalOpen(true);
              }}
              title="Click to change time slot"
            >
              {slot.title || "Time Slot"}
            </span>
          );
        }
        
        // No slot assigned - make it clickable
        return (
          <span 
            className="table-avatar"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedApplicantForSlot(record);
              setSelectSlotModalOpen(true);
            }}
            title="Click to assign time slot"
          >
            <Tag color="default" style={{ cursor: "pointer" }}>Not Assigned</Tag>
          </span>
        );
      },
    }] : []),
    {
      title: "Resume",
      dataIndex: "resume",
      render: (text, record) => (
        <Link to={text} className="btn btn-sm btn-light" target="_blank" rel="noopener noreferrer" style={{ backgroundColor: 'white', }}>
          <i className="fa fa-download" style={{ color: 'red' }} />
        </Link>
      ),
      sorter: (a, b) => a.applicants?.length - b.applicants?.length,
    },
    {
      title: "Export",
      dataIndex: "export",
      render: (text, record) => (
        <button 
          className="btn btn-sm btn-light" 
          onClick={() => handleExportResume(record.id)}
          title="Export Resume as PDF"
          style={{ backgroundColor: 'white'}}
          disabled={exportLoading[record.id]}
        >
          {exportLoading[record.id] ? (
            <i className="fa fa-spinner fa-spin" style={{ color: 'red' }} />
          ) : (
            <i className="fa fa-download" style={{ color: 'red' }} />
          )}
        </button>
      ),
    },
    {
      title: "Total Score",
      dataIndex: "total_score",
      sorter: true,
    
    },
    {
      title: "Action",
      dataIndex: "status",
      className: "text-end",
      // sorter: true,
      render: (text,record) => (
        <div className="dropdown dropdown-action text-end">
             {/* <Link
              className="btn btn-white btn-sm btn-rounded dropdown-toggle"
              to="#"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i
                className={
                  text === "new"
                    ? "far fa-dot-circle text-purple"
                    : text === "pending"
                    ? "far fa-dot-circle text-info"
                    : text === "approved"
                    ? "far fa-dot-circle text-success"
                    : "far fa-dot-circle text-danger"
                }
              />{" "}
              {text}
            </Link> */}
  
            {/* <div className="dropdown-menu dropdown-menu-right">
              <Link className="dropdown-item" to="#">
                <i className="far fa-dot-circle text-purple" /> Applied
              </Link>
              <Link className="dropdown-item" to="#">
                <i className="far fa-dot-circle text-info" /> Shortlisted
              </Link>
              <Link className="dropdown-item" to="#">
                <i className="far fa-dot-circle text-success" /> Hired
              </Link>
              <Link className="dropdown-item" to="#">
                <i className="far fa-dot-circle text-danger" /> Rejected
              </Link>
            </div> */}
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link
              className="dropdown-item"
              to="#"
              onClick={()=>{
                setSelectedResume(record)
                setResumeStatusModal(true)
              }}
            >
              <i className="fa fa-pencil m-r-5" /> Change Status
            </Link>
            {hasJobDrivePermission && record?.job?.job_drive_enabled && (
              <Link
                className="dropdown-item"
                to="#"
                onClick={async (e) => {
                  e.preventDefault();
                  setSelectedApplicant(record);
                  // Fetch slots for this job
                  setSlotsLoading(true);
                  try {
                    const response = await getJobDriveSlotsApi({ job_id: record.job.id });
                    setJobDriveSlots(response.data || []);
                    setAssignSlotModalOpen(true);
                  } catch (error) {
                    console.error("Error fetching slots:", error);
                    toast.error("Failed to load slots", {
                      position: "top-right",
                      autoClose: 5000,
                    });
                  } finally {
                    setSlotsLoading(false);
                  }
                }}
              >
                <i className="fa fa-calendar m-r-5" /> Assign Time Slot
              </Link>
            )}
          </div>
        </div>
      ),
    },
    
  ];

  const GridView = () => {
    if (loading) {
      return (
        <SkeltonLoader
          number={pageSettings?.pageSize}
          active={true}
          className="col-md-4 col-sm-6 col-12 col-lg-4 col-xl-3 p-2"
        />
      );
    } else {
      return (
        <>
          {resumeData.map((resume, index) => (
            <div
              className="col-12 col-sm-6 col-md-4 col-lg-3"
              key={resume.id}
              style={{
                marginBottom: "16px",
                display: "flex"
              }}
            >
              <div className="card shadow-sm" style={{ 
                borderRadius: "8px", 
                border: "none",
                background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
                margin: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                position: "relative",
                overflow: "visible",
                transition: "all 0.2s ease",
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
              }}
              >
                {/* Edit Button - Top Right */}
                <div 
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    zIndex: 10
                  }}
                >
                  <button
                    onClick={() => {
                      setSelectedResume(resume);
                      setResumeStatusModal(true);
                    }}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      border: "none",
                      background: "rgba(239, 68, 68, 0.1)",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      fontSize: "14px"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#ef4444";
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
                      e.currentTarget.style.color = "#ef4444";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    title="Change Status"
                  >
                    <i className="la la-edit" style={{ fontSize: "12px" }}></i>
                  </button>
                </div>

                <div className="card-body" style={{ 
                  padding: "16px",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column"
                }}>
                  {/* Applicant Name */}
                  <div style={{
                    marginBottom: "12px"
                  }}>
                    <h5
                      style={{
                        color: "#1f2937",
                        fontSize: "16px",
                        fontWeight: "600",
                        marginBottom: "8px",
                        lineHeight: "1.4"
                      }}
                    >
                      {resume?.first_name} {resume?.last_name}
                    </h5>
                  </div>

                  {/* Contact Information */}
                  <div style={{
                    marginBottom: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px"
                  }}>
                    {resume?.email && (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}>
                        <i className="la la-envelope" style={{ fontSize: "12px", color: "#9ca3af", width: "16px" }}></i>
                        <span style={{ fontSize: "12px", color: "#374151", fontWeight: "500" }}>
                          {resume.email}
                        </span>
                      </div>
                    )}
                    {resume?.phone && (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}>
                        <i className="la la-phone" style={{ fontSize: "12px", color: "#9ca3af", width: "16px" }}></i>
                        <span style={{ fontSize: "12px", color: "#374151", fontWeight: "500" }}>
                          {resume.phone}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Job Information */}
                  <div style={{
                    marginBottom: "12px",
                    paddingBottom: "12px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px"
                  }}>
                    {resume?.job?.title && (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}>
                        <i className="la la-briefcase" style={{ fontSize: "12px", color: "#9ca3af", width: "16px" }}></i>
                        <span style={{ fontSize: "12px", color: "#374151", fontWeight: "500" }}>
                          {resume.job.title}
                        </span>
                      </div>
                    )}
                    {resume?.created_at && (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}>
                        <i className="la la-calendar" style={{ fontSize: "12px", color: "#9ca3af", width: "16px" }}></i>
                        <span style={{ fontSize: "12px", color: "#374151", fontWeight: "500" }}>
                          Applied: {formatDateWithTimeHandler(resume.created_at)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Status and Resume Actions */}
                  <div style={{
                    marginTop: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px"
                  }}>
                    {/* Status Badge and Download */}
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px"
                    }}>
                      {resume?.resume && (
                        <Link
                          to={resume.resume}
                          download
                          style={{
                            padding: "4px 8px",
                            borderRadius: "6px",
                            background: "rgba(239, 68, 68, 0.1)",
                            color: "#ef4444",
                            textDecoration: "none",
                            fontSize: "10px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            transition: "all 0.2s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#ef4444";
                            e.currentTarget.style.color = "#fff";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
                            e.currentTarget.style.color = "#ef4444";
                          }}
                          title="Download Resume"
                        >
                          <i className="la la-download" style={{ fontSize: "12px" }}></i>
                        </Link>
                      )}
                      {resume?.status && (
                        <span 
                          className={`badge badge-soft-${resume.status === "applied" ? "warning" : resume.status === "rejected" ? "danger" : "success"}`}
                          style={{
                            fontSize: "10px",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            fontWeight: "600"
                          }}
                        >
                          {resume.status}
                        </span>
                      )}
                    </div>


                    {/* Referral Details */}
                    {resume?.is_internal_referral && resume?.referred_by && (
                      <div style={{
                        padding: "8px",
                        borderRadius: "6px",
                        background: "rgba(16, 185, 129, 0.1)",
                        border: "1px solid rgba(16, 185, 129, 0.2)"
                      }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginBottom: "4px"
                        }}>
                          <i className="la la-user-plus" style={{ fontSize: "12px", color: "#10b981" }}></i>
                          <span style={{ fontSize: "10px", color: "#10b981", fontWeight: "600" }}>
                            Internal Referral
                          </span>
                        </div>
                        <div style={{
                          fontSize: "11px",
                          color: "#374151",
                          marginLeft: "16px"
                        }}>
                          <div style={{ fontWeight: "500" }}>
                            {resume.referred_by.first_name} {resume.referred_by.last_name}
                          </div>
                          {resume.referred_by.emp_no && (
                            <div style={{ fontSize: "10px", color: "#6b7280" }}>
                              Emp: {resume.referred_by.emp_no}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      );
    }
  };


  return (
    <div>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="Manage Resumes"
            title="Dashboard"
            subtitle="Job / Manage Resumes"
            modal="#add_resume"
            name="Add Resume"          
            view={view}
            setSalaryModal={setResumeModal}
            setView={setView}
          />

          <ResumeListFilter
           filters={filters} 
           handleInputChange={handleInputChange} 
           searchPlaceholder={"Search Resume"} 
           handleFilterDrawer={handleFilterDrawer}
            handleFilterChange={handleFilterChange}
            loading={loading}
          />



          {view == "grid" ? (
            <div className="row">    
              <GridView />
              <div className="my-2 d-flex justify-content-end ">
                <div className="col-sm-12 col-md-6 ">
                  <Pagination
                    pageSize={pageSettings?.pageSize}
                    pageSizeOptions={pageSizeOptions}
                    defaultCurrent={1}
                    current={pageSettings?.page}
                    total={pageSettings?.total}
                    itemRender={itemRender}
                    onChange={handleGridChange}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-12">
                <div className="card shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e0e0e0", overflow: "visible" }}>
                  <style>{`
                    .modern-table .ant-table-tbody > tr {
                      background-color: #ffffff !important;
                    }
                    .modern-table .ant-table-tbody > tr:hover > td {
                      background-color: #f5f5f5 !important;
                    }
                    .modern-table .ant-table-thead > tr > th {
                      background-color: #f8f9fa !important;
                      font-weight: 600;
                      color: #333;
                      border-bottom: 2px solid #e0e0e0;
                      padding: 10px 12px;
                      font-size: 13px;
                    }
                    .modern-table .ant-table-tbody > tr > td {
                      border-bottom: 1px solid #f0f0f0;
                      padding: 10px 12px;
                      font-size: 13px;
                    }
                    .modern-table .ant-table-wrapper .ant-table-pagination.ant-pagination {
                      border-top: 1px solid #f0f0f0;
                      background-color: #ffffff;
                      border-bottom-left-radius: 12px;
                      border-bottom-right-radius: 12px;
                    }
                    .modern-table .ant-table-tbody > tr > td:last-child {
                      position: relative !important;
                      overflow: visible !important;
                    }
                    .modern-table .dropdown-action {
                      position: relative !important;
                    }
                    .modern-table .dropdown-menu {
                      z-index: 9999 !important;
                      position: absolute !important;
                      top: 100% !important;
                      right: 0 !important;
                      left: auto !important;
                      margin-top: 0.125rem !important;
                      min-width: 160px;
                    }
                    .modern-table .ant-table-wrapper {
                      overflow: visible !important;
                    }
                    .modern-table .ant-table-body {
                      overflow: visible !important;
                    }
                    .modern-table .ant-table-container {
                      overflow: visible !important;
                    }
                    .modern-table .ant-table {
                      overflow: visible !important;
                    }
                  `}</style>
                  <div className="table-responsive" style={{ overflowX: "auto", overflowY: "visible", position: "relative" }}>
                    <Table
                      className="mb-0 modern-table"
                      style={{ 
                        borderRadius: "12px",
                        minWidth: "100%"
                      }}
                      scroll={{ x: "max-content" }}
                      columns={columns}
                      dataSource={resumeData}
                      rowKey={(record) => record.id}
                      loading={loading}
                      expandable={{
                        expandedRowRender,
                        onExpand: (expanded, record) => {
                          if (expanded && !expandedRowData[record.id]) {
                            fetchApplicantDetails(record.id);
                          }
                        },
                        expandRowByClick: false,
                        expandIcon: ({ expanded, onExpand, record }) => (
                          <span 
                            onClick={e => onExpand(record, e)}
                            style={{ cursor: 'pointer', marginRight: 8 }}
                          >
                            {expanded ? (
                              <i className="fa fa-chevron-down text-primary" />
                            ) : (
                              <i className="fa fa-chevron-right" />
                            )}
                          </span>
                        )
                      }}
                      pagination={{
                        total: pageSettings?.total,
                        pageSize: pageSettings?.pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: pageSizeOptions,
                        itemRender: itemRender,
                        position: "right",
                        current: pageSettings?.page,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${total} items`,
                        style: { padding: "16px" }
                      }}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <AllSalaryAddPopup />
      {/* Delete Modal */}
      <DeleteModal Name="Delete Salary" />

      <FilterDrawer
          searchPlaceholder={"Search Salary"}
          showFilterDrawer={showFilterDrawer}
          handleFilterDrawer={handleFilterDrawer}

          handleFilterChange={handleFilterChange}
          filters={filters}
          handleInputChange={handleInputChange}
          handleApplyFilter={handleApplyFilter}
          filterLoading={filterLoading}
          handleDateChange={handleDateChange}
          handleClearAll={handleClearAll}
          isFilterDirty={isFilterDirty}
/>
      {/* Delete Modal */}

      <ResumeStatusModal getResumeList={getResumeList} selectedResume={selectedResume} setSelectedResume={setSelectedResume}   resumeStatusModal={resumeStatusModal} setResumeStatusModal={setResumeStatusModal} />
      
      {/* Job Drive Assign Slot Modal */}
      {selectedApplicant && selectedApplicant.job && (
        <AssignSlotModal
          open={assignSlotModalOpen}
          onCancel={() => {
            setAssignSlotModalOpen(false);
            setSelectedApplicant(null);
            setJobDriveSlots([]);
          }}
          jobId={selectedApplicant.job.id}
          applicant={selectedApplicant}
          slots={jobDriveSlots}
          onSuccess={() => {
            getResumeList();
          }}
          loading={slotsLoading}
        />
      )}

      {/* Job Drive Select Slot Modal (from Time Slot column) */}
      {selectedApplicantForSlot && selectedApplicantForSlot.job && (
        <SelectSlotModal
          open={selectSlotModalOpen}
          onCancel={() => {
            setSelectSlotModalOpen(false);
            setSelectedApplicantForSlot(null);
          }}
          applicant={selectedApplicantForSlot}
          onSuccess={() => {
            getResumeList();
          }}
        />
      )}

      {/* Add custom CSS for expanded row */}
      <style jsx>{`
        .question-answers {
          padding: 0;
        }
        .qa-item {
          transition: all 0.2s ease;
          border-bottom: 1px solid #eee;
        }
        .qa-item:last-child {
          border-bottom: none;
        }
        .qa-item:hover {
          background-color: #f9f9f9;
        }
        .compact-card .ant-card-head {
          min-height: 36px;
        }
        .compact-card .ant-card-head-title {
          padding: 4px 0;
        }
        .compact-card .ant-card-body {
          padding: 8px 12px;
        }
        .dropdown-menu {
          z-index: 10000 !important;
        }
        /* Ensure consistent right alignment for filter and grid toggle */
        .content.container-fluid .col-auto[style*="paddingRight: 0"] {
          padding-right: 0 !important;
        }
        .content.container-fluid .row.align-items-center.g-3 .col-lg-3.d-flex.justify-content-end {
          padding-right: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default AllResume;
